import {
  FC,
  PropsWithChildren,
  createContext,
  useEffect,
  useState,
} from "react";
import { ISocketIndentifier, ISocketContext } from "./SocketProvider.interface";

interface SocketProviderProps extends PropsWithChildren {
  userId?: number;
  chatToken?: string;
  broadcastId?: number;
  studioToken?: string;
}

const socketUrl = String(process.env.REACT_APP_API_ENDPOINT).replace("https://", "");

export const SocketContext = createContext<ISocketContext>({
  webSocket: null,
  connectToChatSocket: () => {},
  connectToStudioSocket: () => {},
});

export const SocketProvider: FC<SocketProviderProps> = ({ children, userId, chatToken, broadcastId, studioToken }) => {
  const [webSocket, setWebSocket] = useState<ISocketContext["webSocket"]>(null);
  const [chatSocketIdentifier, setChatSocketIdentifier] = useState("");
  const [studioSocketIdentifier, setStudioSocketIdentifier] = useState("");
  const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';

  const getSerializedSocketIdentifier = ({
    command,
    identifier,
  }: ISocketIndentifier) => {
    return JSON.stringify({
      command,
      identifier: JSON.stringify(identifier),
    });
  };

  const connectToChatSocket = (broadcastId: number) => {
    if (!webSocket || webSocket.readyState !== webSocket.OPEN) return;

    const identifier: ISocketIndentifier["identifier"] = {
      channel: "ChatChannel",
      room: `Room_${broadcastId}`,
      broadcast_id: broadcastId,
    };

    webSocket.send(
      getSerializedSocketIdentifier({
        command: "subscribe",
        identifier,
      })
    );

    setChatSocketIdentifier(JSON.stringify(identifier));
  };

  const connectToStudioSocket = (studioId: number) => {
    if (!webSocket || webSocket.readyState !== webSocket.OPEN) return;

    const identifier: ISocketIndentifier["identifier"] = {
      channel: "StudioChannel",
      room: `Studio_${studioId}`,
      studio_id: studioId,
    };

    webSocket.send(
      getSerializedSocketIdentifier({
        command: "subscribe",
        identifier,
      })
    );

    setStudioSocketIdentifier(JSON.stringify(identifier));
  };

  useEffect(() => {
    if (!userId && !studioToken) return;

    if (webSocket) {
      webSocket.close();
      setWebSocket(null);
      setChatSocketIdentifier("");
      return;
    }

    const wsUrl = (() => {
      if (chatToken) {
        return `wss://${socketUrl}/websocket?chat_token=${chatToken}`;
      } else if (studioToken) {
        return `${wsProtocol}://${socketUrl}/websocket?studio_token=${studioToken}`;
      } else {
        return `wss://${socketUrl}/websocket`;
      }
    })();

    const socket = new WebSocket(wsUrl);

    const handleSocketOpen = () => {
      socket.send(
        getSerializedSocketIdentifier({
          command: "subscribe",
          identifier: {
            channel: "BroadcastChannel",
            room: `User_${userId}`,
            user_id: userId,
          },
        })
      );

      if (broadcastId) {
        socket.send(
          getSerializedSocketIdentifier({
            command: "subscribe",
            identifier: {
              channel: "ChatChannel",
              room: `Room_${broadcastId}`,
              broadcast_id: broadcastId,
            },
          })
        );
      }
    };

    const handleSocketClose = () => {
      setWebSocket(null);
      setChatSocketIdentifier("");
    };

    socket.addEventListener("open", handleSocketOpen);
    socket.addEventListener("close", handleSocketClose);

    setWebSocket(socket);

    return () => {
      socket.close();
      socket.removeEventListener("open", handleSocketOpen);
      socket.removeEventListener("close", handleSocketClose);
    };
  }, [userId]);

  return (
    <SocketContext.Provider
      value={{
        webSocket,
        chatSocketIdentifier,
        studioSocketIdentifier,
        connectToStudioSocket,
        connectToChatSocket,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export default SocketProvider;
